import { z } from 'zod'
import { CountryIso3Schema } from '../primitives'
import {
  areaExtentKm,
  areaGeometryIssues,
  areaPointCount,
  AreaSchema,
  MAX_AREAS,
  MAX_TOTAL_AREA_POINTS,
  type Area,
} from './geo'
import { canonicalHash } from './hash'
import { expandCitizenship, RegionIdSchema, REGION_IDS } from './regions'
import { ResidenceAcceptedSourcesSchema } from './residence'

/**
 * Membership requirements of an ORGANIZATION, v2 (PLAN v4 §1.1 as amended by §11): five combinable modules.
 *
 *   contact           email always (login, recovery, invitations) + phone off / optional / required
 *   uniqueHuman       liveness + per-organization face de-duplication (implied by identityDocument)
 *   identityDocument  ID document (types) + liveness + face match
 *   citizenship       document nationality ∈ regions ∪ countries (needs identityDocument)
 *   residence         evidence (PoA, document address, GPS, IP) inside 1–20 areas
 *   age               min / max age (document DOB with identityDocument, else declared)
 *
 * Stored in `organizations.requirements` (jsonb). Rows written before v4 (v1: `{countries, phone, minAge, …}`) are
 * upgraded by the 0003 migration and, for safety, on every read (`parseRequirements`). Request bodies also accept
 * the v1 shape (`RequirementsSchema` upgrades it) so older clients keep working.
 */

export const PHONE_REQUIREMENTS = ['off', 'optional', 'required'] as const
export const PhoneRequirementSchema = z.enum(PHONE_REQUIREMENTS)
export type PhoneRequirement = z.infer<typeof PhoneRequirementSchema>

export const VPN_POLICIES = ['review', 'ignore'] as const
export const VpnPolicySchema = z.enum(VPN_POLICIES)
export type VpnPolicy = z.infer<typeof VpnPolicySchema>

export const DEFAULT_FACE_MATCH_AUTO_APPROVE = 95
/** Fixed (PLAN v2 §0): below this Didit declines the face match (completed negative ⇒ final). */
export const FACE_MATCH_DECLINE_THRESHOLD = 50
/** Fixed (PLAN v2 §0): liveness method. */
export const LIVENESS_METHOD = 'ACTIVE_3D' as const
export const MAX_KYC_ATTEMPTS = 3

/** Didit OCR document types an organization can accept: national ID card, passport, residence permit. */
export const DOCUMENT_TYPES = ['ID', 'P', 'RP'] as const
export const DocumentTypeSchema = z.enum(DOCUMENT_TYPES)
export type DocumentType = z.infer<typeof DocumentTypeSchema>
export const DOCUMENT_TYPE_LABELS: Record<DocumentType, string> = {
  ID: 'National ID card',
  P: 'Passport',
  RP: 'Residence permit',
}

/** Max explicit citizenship countries (every ISO3 code fits). */
export const MAX_CITIZENSHIP_COUNTRIES = 260

/**
 * Requirement modules (exemptions, rule-change previews, assurance badges). `age` is not a module: it is always
 * evaluated live from the date of birth, as before v4.
 */
export const MODULE_IDS = ['phone', 'uniqueHuman', 'identityDocument', 'citizenship', 'residence'] as const
export const ModuleIdSchema = z.enum(MODULE_IDS)
export type ModuleId = z.infer<typeof ModuleIdSchema>
export const MODULE_LABELS: Record<ModuleId, string> = {
  phone: 'Verified phone',
  uniqueHuman: 'Unique human (selfie video)',
  identityDocument: 'Identity document',
  citizenship: 'Citizenship',
  residence: 'Residence area',
}

/* ------------------------------------------------------------------ v2 schema */

const ContactSchema = z.object({
  /** Phone collection: off, optional, or required (confirmed with a code). Email is always required. */
  phone: PhoneRequirementSchema.default('off'),
})

/**
 * How long Didit keeps the image-free face template of a member after their verification session is deleted (right
 * after the final decision): `none` = the template goes with the session (the face is only compared with sessions
 * Didit still holds, i.e. reviews in progress), else months. Applies to every organization with liveness (unique
 * human or identity document). Lowering it purges the templates already older than the new period
 * (services/didit-retention.ts); raising it only affects later deletions. ID-number duplicates are detected in any case.
 */
export const FACE_TEMPLATE_RETENTIONS = ['none', 1, 6, 12, 24] as const
export const FaceTemplateRetentionSchema = z.union([
  z.literal('none'),
  z.literal(1),
  z.literal(6),
  z.literal(12),
  z.literal(24),
])
export type FaceTemplateRetention = z.infer<typeof FaceTemplateRetentionSchema>
export const DEFAULT_FACE_TEMPLATE_RETENTION: FaceTemplateRetention = 12
/** Template retention in days for Didit (`face_retention_days`); null = no template. */
export const faceTemplateRetentionDays = (r: FaceTemplateRetention): number | null =>
  r === 'none' ? null : ({ 1: 30, 6: 182, 12: 365, 24: 730 } as const)[r]
export const FACE_TEMPLATE_RETENTION_LABELS: Record<`${FaceTemplateRetention}`, string> = {
  none: 'Not kept (deleted with the verification)',
  '1': '1 month',
  '6': '6 months',
  '12': '12 months',
  '24': '24 months',
}

const UniqueHumanSchema = z.object({
  /** Liveness (ACTIVE_3D) + per-organization duplicate-face check. Implied when identityDocument is enabled. */
  enabled: z.boolean().default(true),
  /**
   * Face template retention after the verification is deleted (also used by the identity document module). Absent ⇒
   * DEFAULT_FACE_TEMPLATE_RETENTION (rows stored before it existed): read it with `faceTemplateRetentionOf`.
   */
  faceTemplateRetention: FaceTemplateRetentionSchema.optional(),
})

const IdentityDocumentSchema = z.object({
  enabled: z.boolean().default(true),
  /** Accepted document types (≥1). */
  documentTypes: z
    .array(DocumentTypeSchema)
    .min(1, 'Select at least one document type')
    .max(DOCUMENT_TYPES.length)
    .refine((t) => new Set(t).size === t.length, 'Duplicate document type')
    .default(['ID', 'P']),
  /** Face-match score (0–100) at or above which Didit approves automatically; below ⇒ review. */
  faceMatchAutoApprove: z
    .number()
    .int()
    .min(FACE_MATCH_DECLINE_THRESHOLD + 1)
    .max(100)
    .default(DEFAULT_FACE_MATCH_AUTO_APPROVE),
})

const CitizenshipSchema = z.object({
  enabled: z.boolean().default(false),
  /** Region presets (domain/regions.ts), expanded at evaluation time. */
  regions: z.array(RegionIdSchema).max(REGION_IDS.length).default([]),
  /** Explicit countries (ISO3), in addition to the regions. */
  countries: z.array(CountryIso3Schema).max(MAX_CITIZENSHIP_COUNTRIES).default([]),
})

export const ResidenceRequirementSchema = z.object({
  enabled: z.boolean().default(false),
  /** Union of areas (1–20 when enabled), stored frozen. */
  areas: z.array(AreaSchema).max(MAX_AREAS).default([]),
  /** Evidence the organizer accepts (≥1 when enabled). */
  evidence: ResidenceAcceptedSourcesSchema.prefault({}),
  /** Proof of address: maximum document age in months (Didit `age_months`). */
  proofOfAddressMaxAgeMonths: z.number().int().min(1).max(12).default(3),
})
export type ResidenceRequirement = z.infer<typeof ResidenceRequirementSchema>

const AgeSchema = z.object({
  /**
   * Minimum age, checked at the start of every voting (age at the voting start). Not a condition to join: an
   * under-age member becomes eligible for votings that start after their birthday.
   */
  minAge: z.number().int().min(0).max(120).nullable().default(18),
  /** Maximum age (above it at registration ⇒ final decline). */
  maxAge: z.number().int().min(0).max(150).nullable().default(null),
})

export interface RequirementsIssue {
  path: (string | number)[]
  message: string
}

export interface RequirementsIssuesOptions {
  /**
   * Check the geometric validity of the areas (`areaGeometryIssues`: drawn polygons simple with holes inside, no
   * zero-area ring). Default true (request bodies, drafts, publishing); false for STORED requirements
   * (`RequirementsV2Schema`, `parseRequirements`), so rows saved before the check keep parsing.
   */
  geometry?: boolean
}

/**
 * Consistency of a v2 requirements object (the superRefine of RequirementsV2Schema, also usable on a draft):
 * citizenship ⇒ identityDocument and ≥1 country after expansion; residence ⇒ ≥1 area and ≥1 accepted evidence;
 * document-address evidence ⇒ identityDocument; total area points; valid area geometries when residence is enabled
 * (opts.geometry); maxAge ≥ minAge.
 */
export function requirementsIssues(r: RequirementsV2, opts: RequirementsIssuesOptions = {}): RequirementsIssue[] {
  const issues: RequirementsIssue[] = []
  if (r.citizenship.enabled) {
    if (!r.identityDocument.enabled)
      issues.push({ path: ['citizenship', 'enabled'], message: 'Citizenship needs the identity document check' })
    if (!expandCitizenship(r.citizenship.regions, r.citizenship.countries).length)
      issues.push({ path: ['citizenship', 'countries'], message: 'Select at least one country or region' })
  }
  if (r.residence.enabled) {
    if (!r.residence.areas.length) issues.push({ path: ['residence', 'areas'], message: 'Add at least one area' })
    const ev = r.residence.evidence
    if (!ev.proofOfAddress && !ev.documentAddress && !ev.gps && !ev.ip)
      issues.push({ path: ['residence', 'evidence'], message: 'Accept at least one kind of residence evidence' })
    if (ev.documentAddress && !r.identityDocument.enabled)
      issues.push({
        path: ['residence', 'evidence', 'documentAddress'],
        message: 'The address on the ID document needs the identity document check',
      })
  }
  const points = r.residence.areas.reduce((n, a) => n + areaPointCount(a), 0)
  if (points > MAX_TOTAL_AREA_POINTS)
    issues.push({
      path: ['residence', 'areas'],
      message: `Areas too detailed: ${points} points in total (max ${MAX_TOTAL_AREA_POINTS})`,
    })
  // Areas of a disabled module are kept but unused (and not editable in the builder): checked once it is enabled.
  if (opts.geometry !== false && r.residence.enabled)
    r.residence.areas.forEach((a, i) => {
      for (const issue of areaGeometryIssues(a))
        issues.push({ path: ['residence', 'areas', i, ...issue.path], message: `${a.label}: ${issue.message}` })
    })
  if (r.age.minAge != null && r.age.maxAge != null && r.age.maxAge < r.age.minAge)
    issues.push({ path: ['age', 'maxAge'], message: 'Maximum age must be ≥ minimum age' })
  return issues
}

const RequirementsV2ObjectSchema = z.object({
  version: z.literal(2).default(2),
  contact: ContactSchema.prefault({}),
  uniqueHuman: UniqueHumanSchema.prefault({}),
  identityDocument: IdentityDocumentSchema.prefault({}),
  citizenship: CitizenshipSchema.prefault({}),
  residence: ResidenceRequirementSchema.prefault({}),
  age: AgeSchema.prefault({}),
  /** Collect location data: Didit IP analysis + browser GPS at registration and vote time (members map). */
  collectLocation: z.boolean().default(false),
  /** What to do on VPN / Tor / data-center IPs. */
  vpnPolicy: VpnPolicySchema.default('review'),
})
export type RequirementsV2 = z.infer<typeof RequirementsV2ObjectSchema>

/**
 * STORED / returned requirements: consistency rules without the area-geometry validity check (older rows keep
 * parsing). Request bodies go through `RequirementsSchema`, which adds it.
 */
export const RequirementsV2Schema = RequirementsV2ObjectSchema.superRefine((r, ctx) => {
  for (const i of requirementsIssues(r, { geometry: false })) ctx.addIssue({ code: 'custom', ...i })
})

/** Request-body v2 requirements: every consistency rule, area-geometry validity included. */
const RequirementsV2BodySchema = RequirementsV2ObjectSchema.superRefine((r, ctx) => {
  for (const i of requirementsIssues(r)) ctx.addIssue({ code: 'custom', ...i })
})
export type RequirementsV2Draft = z.input<typeof RequirementsV2Schema>

/** New organizations: email + unique human + ID document (ID card or passport), no citizenship / residence. */
export const defaultRequirements = (): RequirementsV2 => RequirementsV2Schema.parse({})

/* ------------------------------------------------------------------ v1 (legacy) */

/**
 * v1 requirements (PLAN v3), as stored before v4: identity document = national ID card of one of `countries`,
 * which were also the accepted nationalities. Kept to upgrade old rows / bodies.
 */
export const RequirementsV1Schema = z.object({
  countries: z.array(CountryIso3Schema).max(MAX_CITIZENSHIP_COUNTRIES).default([]),
  phone: PhoneRequirementSchema.default('off'),
  minAge: z.number().int().min(0).max(120).nullable().default(18),
  maxAge: z.number().int().min(0).max(150).nullable().default(null),
  collectLocation: z.boolean().default(false),
  vpnPolicy: VpnPolicySchema.default('review'),
  faceMatchAutoApprove: z
    .number()
    .int()
    .min(FACE_MATCH_DECLINE_THRESHOLD + 1)
    .max(100)
    .default(DEFAULT_FACE_MATCH_AUTO_APPROVE),
})
export type RequirementsV1 = z.infer<typeof RequirementsV1Schema>

/**
 * v1 → v2 (same rule in SQL: apps/api/drizzle/0003_*.sql): document ID card only (as before), unique human on,
 * citizenship = the v1 countries (enabled when any), no residence; phone / ages / location / VPN / face threshold
 * unchanged. An inconsistent v1 age range (maxAge < minAge, impossible through the v1 schema) is kept as is.
 */
export function upgradeRequirementsV1(v1: RequirementsV1): RequirementsV2 {
  return {
    version: 2,
    contact: { phone: v1.phone },
    uniqueHuman: { enabled: true },
    identityDocument: { enabled: true, documentTypes: ['ID'], faceMatchAutoApprove: v1.faceMatchAutoApprove },
    citizenship: { enabled: v1.countries.length > 0, regions: [], countries: [...v1.countries] },
    residence: {
      enabled: false,
      areas: [],
      evidence: { proofOfAddress: true, documentAddress: false, gps: false, ip: false },
      proofOfAddressMaxAgeMonths: 3,
    },
    age: { minAge: v1.minAge, maxAge: v1.maxAge },
    collectLocation: v1.collectLocation,
    vpnPolicy: v1.vpnPolicy,
  }
}

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v)
const V1_KEYS = ['countries', 'phone', 'minAge', 'maxAge', 'faceMatchAutoApprove'] as const

/**
 * REQUEST-body detection: an object without `version` that carries v1 keys (`countries`, top-level `phone` / ages, …).
 * A new-organization body `{}` is NOT v1 (it means "v2 defaults").
 */
export const isRequirementsV1 = (raw: unknown): boolean =>
  isRecord(raw) && raw.version === undefined && V1_KEYS.some((k) => k in raw)

/**
 * STORED-object detection, aligned with the SQL migration 0003 (`WHERE NOT (requirements ? 'version')`): every stored
 * plain object without `version` predates v4 and is v1 (`{}` and `{collectLocation: true}` included).
 */
export const isStoredRequirementsV1 = (raw: unknown): boolean => isRecord(raw) && raw.version === undefined

/**
 * STORED requirements (DB reads, fixtures with v1 shapes) → v2. Unversioned objects are validated as v1 and upgraded
 * (same mapping as the SQL migration); versioned objects are validated as v2; null / undefined ⇒ v2 defaults.
 * Throws a ZodError when invalid (fail closed: an unreadable rule set must not silently relax membership checks).
 */
export function parseRequirements(raw: unknown): RequirementsV2 {
  if (raw === null || raw === undefined) return RequirementsV2Schema.parse({})
  if (isStoredRequirementsV1(raw))
    return RequirementsV2Schema.parse(upgradeRequirementsV1(RequirementsV1Schema.parse(raw)))
  return RequirementsV2Schema.parse(raw)
}

/**
 * Requirements in REQUEST bodies (create / update organization): v2, or v1 upgraded on the fly (compatibility).
 * Typed as the v2 schema; at runtime a v1-shaped object (key-based detection, `isRequirementsV1`) is validated as v1
 * and upgraded before validation; an INVALID v1 object is rejected with the v1 issues (never silently read as v2
 * defaults, which would relax the rules).
 */
export const RequirementsSchema = z.preprocess((raw, ctx) => {
  if (!isRequirementsV1(raw)) return raw
  const v1 = RequirementsV1Schema.safeParse(raw)
  if (v1.success) return upgradeRequirementsV1(v1.data)
  for (const issue of v1.error.issues)
    ctx.addIssue({ code: 'custom', message: issue.message, path: issue.path as (string | number)[] })
  return z.NEVER
}, RequirementsV2BodySchema) as unknown as typeof RequirementsV2Schema

/** Requirements as the API and the web app use them (always v2). */
export type Requirements = RequirementsV2
export type RequirementsDraft = RequirementsV2Draft

/* ------------------------------------------------------------------ derived views */

/** What an organization effectively asks for (uniqueHuman is implied by the document check). */
export interface EffectiveModules {
  phone: boolean
  uniqueHuman: boolean
  identityDocument: boolean
  citizenship: boolean
  residence: boolean
  /** A Didit verification session is needed at all (else contact codes + local policy only). */
  didit: boolean
}

export function effectiveModules(r: RequirementsV2): EffectiveModules {
  const identityDocument = r.identityDocument.enabled
  const uniqueHuman = r.uniqueHuman.enabled || identityDocument
  const citizenship = r.citizenship.enabled
  const residence = r.residence.enabled
  const poa = residence && r.residence.evidence.proofOfAddress
  return {
    phone: r.contact.phone === 'required',
    uniqueHuman,
    identityDocument,
    citizenship,
    residence,
    didit: uniqueHuman || identityDocument || poa,
  }
}

/** The organization's face template retention (default when not set). */
export const faceTemplateRetentionOf = (r: Pick<RequirementsV2, 'uniqueHuman'>): FaceTemplateRetention =>
  r.uniqueHuman.faceTemplateRetention ?? DEFAULT_FACE_TEMPLATE_RETENTION

/** The organization checks a minimum or maximum age (the date of birth is then kept after the decision). */
export const hasAgeLimits = (r: RequirementsV2): boolean => r.age.minAge != null || r.age.maxAge != null

/** Expanded accepted citizenships (empty when the citizenship module is off). */
export const acceptedCitizenships = (r: RequirementsV2): string[] =>
  r.citizenship.enabled ? expandCitizenship(r.citizenship.regions, r.citizenship.countries) : []

/** Hash of a requirements object (kyc_sessions.requirements_snapshot comparisons, assurance.requirementsHash). */
export const requirementsHash = (r: RequirementsV2): string => canonicalHash(r)

/* ------------------------------------------------------------------ warnings (R15) */

export const REQUIREMENTS_WARNING_CODES = [
  'basic_only', // email (+phone) only: one human with N inboxes gets N ballot slots (R5)
  'age_declared_only', // age limits without the document check: self-declared date of birth
  'residence_weak_sources_only', // only GPS / IP accepted: spoofable / approximate evidence decides
  'residence_ip_only_small_area', // only IP accepted and an area < 100 km across: almost everyone goes to review
  'residence_document_address_rare', // document address accepted: many documents carry no address (passports never)
  'citizenship_residence_permit', // residence permits accepted with citizenship: a permit proves no citizenship
  'residence_default_area_label', // an area still named "Drawn …" by the map editor: labels are public (landing)
] as const
export type RequirementsWarningCode = (typeof REQUIREMENTS_WARNING_CODES)[number]

/** Prefix of the labels the area editor gives to drawn shapes ("Drawn polygon", "Drawn circle", …). */
export const DEFAULT_DRAWN_AREA_LABEL_PREFIX = 'Drawn '

/** The area still carries the editor's default label (residence_default_area_label warning). */
export const isDefaultAreaLabel = (label: string): boolean => label.trim().startsWith(DEFAULT_DRAWN_AREA_LABEL_PREFIX)

export interface RequirementsWarning {
  code: RequirementsWarningCode
  path: (string | number)[]
  message: string
}

/** IP evidence is ±50 km: areas smaller than this send IP-only members to review. */
export const IP_MIN_USEFUL_AREA_KM = 100

/**
 * Non-blocking warnings for the organizer (settings save / publish dialog): configurations that will send most
 * members to review or that give weak assurance.
 */
export function requirementsWarnings(r: RequirementsV2): RequirementsWarning[] {
  const out: RequirementsWarning[] = []
  const m = effectiveModules(r)
  if (!m.uniqueHuman && !m.identityDocument)
    out.push({
      code: 'basic_only',
      path: ['uniqueHuman', 'enabled'],
      message:
        'Only email (and phone) are checked: one person with several email addresses can join several times and vote once per membership.',
    })
  if (!m.identityDocument && (r.age.minAge != null || r.age.maxAge != null))
    out.push({
      code: 'age_declared_only',
      path: ['age'],
      message: 'Without the identity document check, age limits rely on the date of birth members declare.',
    })
  if (r.residence.enabled) {
    const ev = r.residence.evidence
    if (!ev.proofOfAddress && !ev.documentAddress && (ev.gps || ev.ip))
      out.push({
        code: 'residence_weak_sources_only',
        path: ['residence', 'evidence'],
        message: 'Only browser location / IP are accepted: they are approximate and easy to fake.',
      })
    if (ev.ip && !ev.proofOfAddress && !ev.documentAddress && !ev.gps) {
      const small = r.residence.areas.filter((a: Area) => a.kind !== 'place' || !a.countryIso3)
      if (small.some((a) => areaExtentKm(a) < IP_MIN_USEFUL_AREA_KM))
        out.push({
          code: 'residence_ip_only_small_area',
          path: ['residence', 'areas'],
          message:
            'IP location is only accurate to about 50 km: with small areas and only IP accepted, almost every member will need a manual review.',
        })
    }
    if (ev.documentAddress)
      out.push({
        code: 'residence_document_address_rare',
        path: ['residence', 'evidence', 'documentAddress'],
        message: 'Many ID documents carry no address (passports never do): those members will need another evidence.',
      })
    r.residence.areas.forEach((a, i) => {
      if (isDefaultAreaLabel(a.label))
        out.push({
          code: 'residence_default_area_label',
          path: ['residence', 'areas', i, 'label'],
          message: `The area “${a.label}” still has its default name: area names are shown publicly on the join page, give it a meaningful one.`,
        })
    })
  }
  if (r.citizenship.enabled && r.identityDocument.documentTypes.includes('RP'))
    out.push({
      code: 'citizenship_residence_permit',
      path: ['identityDocument', 'documentTypes'],
      message: 'A residence permit does not prove citizenship: holders are checked by the nationality printed on it.',
    })
  return out
}
